import { useState, useEffect, type ReactNode } from "react";

const STORAGE_KEY = "tbf_beta_unlocked";
const EXPECTED = import.meta.env.VITE_BETA_GATE_PASSWORD as string | undefined;

/**
 * BetaGate — Closed-beta site protection
 *
 * Only active when VITE_BETA_GATE_PASSWORD is set in the environment.
 * If the env var is missing or empty, the gate is transparent (always passes).
 *
 * Correct password is stored in sessionStorage so it survives page refreshes
 * within the same browser session but clears on tab close.
 */

function isUnlocked(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistUnlock(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, "1");
  } catch { /* storage blocked */ }
}

export default function BetaGate({ children }: { children: ReactNode }) {
  const gateActive = Boolean(EXPECTED && EXPECTED.trim().length > 0);
  const [unlocked, setUnlocked] = useState(!gateActive || isUnlocked());
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (!gateActive) setUnlocked(true);
  }, [gateActive]);

  if (unlocked) return <>{children}</>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() === EXPECTED) {
      persistUnlock();
      setUnlocked(true);
    } else {
      setError(true);
      setShaking(true);
      setInput("");
      setTimeout(() => setShaking(false), 500);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3] flex flex-col items-center justify-center px-4">
      {/* Brand mark */}
      <div className="mb-10 flex flex-col items-center gap-2">
        <span
          className="text-2xl font-light tracking-[0.3em] text-[#2C2C2C] uppercase"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          TaBiFoTo
        </span>
        <span className="text-[10px] tracking-[0.5em] text-[#2C2C2C]/30 uppercase font-light">
          Closed Beta
        </span>
      </div>

      {/* Gate card */}
      <form
        onSubmit={handleSubmit}
        className={`bg-white rounded-2xl border border-[#E8E0D5] w-full max-w-sm px-8 py-8 flex flex-col gap-5 ${shaking ? "animate-[shake_0.4s_ease-in-out]" : ""}`}
        style={
          shaking
            ? {
                animation: "shake 0.4s ease-in-out",
              }
            : undefined
        }
      >
        <div>
          <h1 className="text-base font-light text-[#2C2C2C] tracking-wide mb-1">
            Enter Beta Access Password
          </h1>
          <p className="text-xs font-light text-[#2C2C2C]/40">
            This preview is restricted to authorized testers only.
          </p>
        </div>

        <div className="relative">
          <input
            type={showPw ? "text" : "password"}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setError(false);
            }}
            placeholder="Password"
            autoFocus
            autoComplete="current-password"
            className={`w-full px-4 py-3 pr-10 rounded-lg border text-sm font-light text-[#2C2C2C] focus:outline-none transition-colors ${
              error
                ? "border-red-300 bg-red-50/30"
                : "border-[#D9D9D9] bg-[#F7F6F3] focus:border-[#C8B8A6]"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#2C2C2C]/30 hover:text-[#2C2C2C]/60 transition-colors cursor-pointer"
          >
            <i className={`${showPw ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
          </button>
        </div>

        {error && (
          <p className="text-xs font-light text-red-500 -mt-2 flex items-center gap-1.5">
            <i className="ri-error-warning-line text-sm" />
            Incorrect password. Please try again.
          </p>
        )}

        <button
          type="submit"
          disabled={!input.trim()}
          className="w-full py-3 bg-[#2C2C2C] text-white text-sm font-light rounded-lg tracking-widest uppercase hover:bg-[#3C3C3C] transition-colors disabled:opacity-40 cursor-pointer whitespace-nowrap"
        >
          Enter
        </button>
      </form>

      <p className="mt-8 text-[11px] font-light text-[#2C2C2C]/20 text-center">
        Unauthorized access is prohibited.
      </p>

      {/* Shake keyframe injection */}
      <style>{`
        @keyframes shake {
          0%   { transform: translateX(0); }
          15%  { transform: translateX(-6px); }
          30%  { transform: translateX(6px); }
          45%  { transform: translateX(-4px); }
          60%  { transform: translateX(4px); }
          75%  { transform: translateX(-2px); }
          90%  { transform: translateX(2px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
